import { clsx } from 'clsx'
import { type FC } from 'react'

import { ProductDiscountBadge } from '../../ProductDiscountBadge'
import { ProductPrescriptionBonusBadge } from '../../ProductPrescriptionBonusBadge'
import { ProductRedPointsBadge } from '../../ProductRedPointsBadge'
import { ProductCarouselThumbnails } from '../ProductCarouselThumbnails'

import { ProductCarouselGalleryWithSideButtons } from './ProductCarouselGalleryWithSideButtons'
import { type ProductCarouselGalleryWithThumbnailsProps } from './ProductCarouselGalleryWithThumbnailsProps'
import { ProductCarouselVideoBadge } from './ProductCarouselVideoBadge'
import { useCarouselGalleryControls } from './useCarouselGalleryControls'
import { useRedPointsAmount } from './useRedPointsAmount'

const ProductCarouselGalleryWithThumbnails: FC<
  ProductCarouselGalleryWithThumbnailsProps
> = ({
  images,
  initialImageIndex,
  largeCarouselBadgesGroup,
  onIndexChanged,
  onItemClick,
  onVideoBadgeClicked,
  playVideoOnClick,
  playVideoOnScrolledIntoView,
  productName,
  showSimilarImagesNotice,
  showVideoBadgeIfVideoAvailable,
  thumbnails,
  useLargeImageSizes,
  videos,
  withLeftRightButtons = false,
  zoomEnabled,
}) => {
  const controls = useCarouselGalleryControls({
    images,
    initialImageIndex,
    onIndexChanged,
    onVideoBadgeClicked,
    videos,
  })
  const redPointsAmount = useRedPointsAmount()

  return (
    <>
      <ProductCarouselGalleryWithSideButtons
        currentImageIndex={controls.currentImageIndex}
        images={images}
        onImageScrolledInView={controls.handleImageScrolledIntoView}
        onItemClick={onItemClick}
        onSideButtonClick={controls.handleGallerySideButtonClick}
        playVideoOnClick={playVideoOnClick}
        playVideoOnScrolledIntoView={playVideoOnScrolledIntoView}
        productName={productName}
        showSimilarImagesNotice={showSimilarImagesNotice}
        useLargeImageSizes={useLargeImageSizes}
        videos={videos}
        withLeftRightButtons={withLeftRightButtons}
        zoomEnabled={zoomEnabled}
      />
      {thumbnails === 'none' ? null : (
        <ProductCarouselThumbnails
          currentImageIndex={controls.currentImageIndex}
          images={images}
          onClick={controls.handleRequestJumpToIndex}
          productName={productName}
          thumbnails={thumbnails}
          videos={videos}
        />
      )}
      <div
        className={clsx(
          'absolute flex flex-col-reverse',
          largeCarouselBadgesGroup
            ? 'right-6 top-0'
            : 'right-12 top-10 tablet:right-24',
        )}
      >
        {showVideoBadgeIfVideoAvailable && videos.length > 0 ? (
          <>
            <ProductCarouselVideoBadge
              className="pointer-events-none hidden tablet:pointer-events-auto tablet:right-16 tablet:flex"
              onClick={(): void => {
                controls.handleVideoBadgeClick()
              }}
            />
            <ProductCarouselVideoBadge
              className="tablet:pointer-events-none tablet:right-16 tablet:hidden"
              onClick={(): void => {
                controls.handleVideoBadgeClick(true)
              }}
            />
          </>
        ) : null}
        {redPointsAmount ? (
          <ProductRedPointsBadge redPointsAmount={redPointsAmount} />
        ) : null}
        <ProductPrescriptionBonusBadge />
        <ProductDiscountBadge />
      </div>
    </>
  )
}

export { ProductCarouselGalleryWithThumbnails }
